import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import DateBox from 'devextreme-react/date-box';
import { Column } from 'devextreme-react/data-grid';
import { Editing, RequiredRule } from 'devextreme-react/gantt';
import { formatMessage } from 'devextreme/localization';
import { Button } from 'devextreme-react/button';
import { ValidationGroup, Validator } from 'devextreme-react';
import style from './readyProductsTable.module.scss';
import { getCompatibleForm, getRenderField, normalizeDateToSend } from '../../../utils/functions';
import columnDataGrid from './columnDataGrid';
import HeaderButtonMVP from '../../HeaderButtonMVP';
import DataGridMVP from '../../DataGridMVP';
import PopupMVP from '../../PopupMVP';
import convertWeightUnit, { getUnit } from '../../../utils/weight_converter';

const InputModalReadyProducts = ({
  inputReadyProductReducer,
  correctionReadyProductsReducer,
  setZdvlLineNumIdField,
  setQuantityField,
  setPieceIdField,
  setQcIdField,
  setOnModalSaveClick,
  setQuantityOffField,
  setQcDateField,
  setProductUnit,
  setReadyPlantOperationAttribsInherit,
  updateReadyProducts,
  getReadyProducts,
  plantId,
  setInputProductsPieces,
  onClickCb,
  setValidateInstance,
  useFactWeight
}) => {
  const [headButtVisible, setHeadButtVisible] = useState({ display: 'none' });
  const [dataSource, setDataSource] = useState({ selectList: [] });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [dataGridInstance, setDataGridInstance] = useState(null);
  const [editableData, setEditableData] = useState(
    inputReadyProductReducer.inputPieces
  );
  const validatorRef = useRef(null);

  // #region Расчет в ЕИ
  const [isWeightable, setIsWeightable] = useState(false);

  const isWeightUnits = (units) => units.every((unit) => !!getUnit(unit));
  const [zdvlLineNumId, setZdvlLineNumId] = useState(null);
  // const tableRef = useRef(null);

  const productUnitString = useMemo(() => {
    if (inputReadyProductReducer.productUnit) return `, ${inputReadyProductReducer.productUnit}`;
    return '';
  }, [inputReadyProductReducer.productUnit]);

  useEffect(() => {
    let parentUnit = '';
    if (inputReadyProductReducer.productUnit) {
      parentUnit = inputReadyProductReducer.productUnit;
    }
    const mappedUnits = inputReadyProductReducer.inputPieces.map((item) => item.unitShortName);
    let isAllUnitsWeightable;
    if (inputReadyProductReducer.productUnit) {
      isAllUnitsWeightable = !!getUnit(inputReadyProductReducer.productUnit);
    } else {
      isAllUnitsWeightable = isWeightUnits(mappedUnits) && !!getUnit(parentUnit);
    }
    setIsWeightable(isAllUnitsWeightable);
    if (inputReadyProductReducer.zdvlLineNumId) {
      setZdvlLineNumId(inputReadyProductReducer.zdvlLineNumId);
    }
  }, [
    inputReadyProductReducer.productUnit,
    inputReadyProductReducer.inputPieces,
    inputReadyProductReducer.zdvlLineNumId
  ]);

  // #endregion [Расчет в ЕИ]
  const closeHeaderButton = useCallback(() => {
    dataGridInstance.instance.clearSelection();
    setHeadButtVisible({ display: 'none' });
  }, [dataGridInstance]);

  const onSelectionChanged = useCallback(({ selectedRowsData }) => {
    if (selectedRowsData.length) {
      setHeadButtVisible({ display: 'flex' });
    } else {
      setHeadButtVisible({ display: 'none' });
    }
    setDataSource((item) => ({
      ...item,
      selectList: selectedRowsData
    }));
  }, []);

  const renderMark = useMemo(
    () =>
      getRenderField({
        state: inputReadyProductReducer.mark,
        form: 'string'
      }),
    [inputReadyProductReducer.mark]
  );

  const renderNormativeDoc = useMemo(
    () =>
      getRenderField({
        state: inputReadyProductReducer.normativeDoc,
        form: 'string'
      }),
    [inputReadyProductReducer.normativeDoc]
  );

  const renderLength = useMemo(
    () =>
      getRenderField({
        state: inputReadyProductReducer.length,
        form: 'number'
      }),
    [inputReadyProductReducer.length]
  );

  const renderPieceId = useMemo(
    () =>
      getRenderField({
        state: inputReadyProductReducer.pieceId,
        setState: setPieceIdField,
        form: 'string'
      }),
    [inputReadyProductReducer.pieceId, setPieceIdField]
  );

  const renderProfileSize = useMemo(
    () =>
      getRenderField({
        state: inputReadyProductReducer.profileSize,
        form: 'string'
      }),
    [inputReadyProductReducer.profileSize]
  );

  const renderQcDate = useMemo(
    () => (
      <DateBox
        value={
          inputReadyProductReducer.qcDate
            ? new Date(inputReadyProductReducer.qcDate)
            : null
        }
        displayFormat="dd.MM.yyyy"
        stylingMode="filled"
        placeholder=""
        onValueChanged={({ value }) =>
          setQcDateField(normalizeDateToSend(value))}
        width={300}
        showClearButton
      />
    ),
    [inputReadyProductReducer.qcDate, setQcDateField]
  );

  const renderQcId = useMemo(
    () =>
      getRenderField({
        state: inputReadyProductReducer.qcId,
        setState: setQcIdField,
        form: 'string'
      }),
    [inputReadyProductReducer.qcId, setQcIdField]
  );

  const renderThickness = useMemo(
    () =>
      getRenderField({
        state: inputReadyProductReducer.thickness,
        form: 'number'
      }),
    [inputReadyProductReducer.thickness]
  );

  const renderWeightOff = useMemo(() =>
    getRenderField({
      state: inputReadyProductReducer.quantityOff ? inputReadyProductReducer.quantityOff : 0,
      form: 'number',
      setState: plantId === '8200'
        ? !!isWeightable && setQuantityOffField
        : !isWeightable && setQuantityOffField,
      validation: (
        <Validator>
          <RequiredRule />
        </Validator>
      )
    }), [inputReadyProductReducer, isWeightable, plantId, setQuantityOffField]);

  const renderWeight = useMemo(
    () =>
      getRenderField({
        state: inputReadyProductReducer.quantity ? inputReadyProductReducer.quantity : 0,
        setState: setQuantityField,
        form: 'number'
      }),
    [inputReadyProductReducer.quantity, setQuantityField]
  );

  const renderZdvlLineId = useMemo(
    () =>
      getRenderField({
        state: [
          ...new Map(
            correctionReadyProductsReducer.readyProducts.map((item) => [
              item.zdvlLineNumId,
              item
            ])
          ).values()
        ].map((el) => ({
          key: `${el.zdvlOrderId} ${el.zdvlLineId}`,
          value: el.zdvlLineNumId
        })),
        setState: (value) => {
          let zdvlUnit = null;
          if (value) {
            zdvlUnit = correctionReadyProductsReducer.readyProducts.find(
              (el) => el.zdvlLineNumId === value
            ).unitShort;
          }
          setZdvlLineNumIdField(value);
          setProductUnit(zdvlUnit);
        },
        value: zdvlLineNumId,
        form: 'array',
        validation: (
          <Validator>
            <RequiredRule />
          </Validator>
        )
      }),
    [
      correctionReadyProductsReducer.readyProducts,
      zdvlLineNumId,
      setZdvlLineNumIdField,
      setProductUnit
    ]
  );

  const renderInheritanceFields = useMemo(() => {
    if (inputReadyProductReducer.plantOperationAttribsInheritDtos
      && inputReadyProductReducer.plantOperationAttribsInheritDtos.length) {
      return inputReadyProductReducer.plantOperationAttribsInheritDtos.map(
        (item) => (
          <div className={style.line} key={item.attribCode}>
            <span>{`${item.attribDescr}${item.unitShort ? `, ${item.unitShort}` : ''}:`}</span>
            {getRenderField({
              state: item.value,
              setState: (value) => {
                const newData = inputReadyProductReducer
                  .plantOperationAttribsInheritDtos
                  .map((data) => item.attribCode === data.attribCode ? ({ ...data, value }) : data);
                setReadyPlantOperationAttribsInherit(newData);
              },
              form: getCompatibleForm(item.sapAttribType)
            })}
          </div>
        )
      );
    }
    return <></>;
  }, [
    inputReadyProductReducer.plantOperationAttribsInheritDtos,
    setReadyPlantOperationAttribsInherit
  ]);

  const calculateWeight = useCallback(
    (data) =>
      data
        .map((el) => inputReadyProductReducer.productUnit !== el.unitShortName
          ? convertWeightUnit(
            el.quantityCurrent + el.quantityOff,
            el.unitShortName,
            inputReadyProductReducer.productUnit
          )
          : el.quantityCurrent + el.quantityOff)
        .reduce((acc, el) => acc + el, 0)
        .toFixed(3),
    [inputReadyProductReducer.productUnit]
  );

  const onClickDelete = useCallback(async () => {
    setDeleteConfirmModal(false);
    const newInputPieces = inputReadyProductReducer.inputPieces.filter(
      (item) => item.pieceNumId !== dataSource.selectList[0].pieceNumId
    );
    const payload = {
      inputPieces: newInputPieces,
      plantOperationAttribsInheritDtos: inputReadyProductReducer.plantOperationAttribsInheritDtos,
      length: inputReadyProductReducer.length,
      mark: inputReadyProductReducer.mark,
      normativeDoc: inputReadyProductReducer.normativeDoc,
      operationNumId: inputReadyProductReducer.operationNumId,
      operationType: inputReadyProductReducer.operationType,
      pieceId: inputReadyProductReducer.pieceId,
      pieceNumId: inputReadyProductReducer.pieceNumId,
      profileSize: inputReadyProductReducer.profileSize,
      qcDate: inputReadyProductReducer.qcDate,
      qcId: inputReadyProductReducer.qcId,
      qty: inputReadyProductReducer.qty,
      thickness: inputReadyProductReducer.thickness,
      zdvlLineId: inputReadyProductReducer.zdvlLineId,
      zdvlLineNumId,
      quantity: inputReadyProductReducer.quantity,
      quantityOff: inputReadyProductReducer.quantityOff,
      unitOfMeasure: inputReadyProductReducer.unitOfMeasure,
      unitShort: inputReadyProductReducer.productUnit
    };
    await updateReadyProducts(payload);
    await getReadyProducts({ plantId });
    onClickCb();
  }, [
    dataSource.selectList,
    getReadyProducts,
    inputReadyProductReducer.inputPieces,
    inputReadyProductReducer.length,
    inputReadyProductReducer.mark,
    inputReadyProductReducer.normativeDoc,
    inputReadyProductReducer.operationNumId,
    inputReadyProductReducer.operationType,
    inputReadyProductReducer.pieceId,
    inputReadyProductReducer.pieceNumId,
    inputReadyProductReducer.plantOperationAttribsInheritDtos,
    inputReadyProductReducer.productUnit,
    inputReadyProductReducer.profileSize,
    inputReadyProductReducer.qcDate,
    inputReadyProductReducer.qcId,
    inputReadyProductReducer.qty,
    inputReadyProductReducer.quantity,
    inputReadyProductReducer.quantityOff,
    inputReadyProductReducer.thickness,
    inputReadyProductReducer.unitOfMeasure,
    inputReadyProductReducer.zdvlLineId,
    onClickCb,
    plantId,
    updateReadyProducts,
    zdvlLineNumId
  ]);

  // useEffect(() => {
  //   if (isWeightable && plantId !== '8200') {
  //     setQuantityOffField(Number(calculateWeight(editableData)));
  //   }
  // }, [
  //   calculateWeight,
  //   editableData,
  //   isWeightable,
  //   plantId,
  //   setQuantityField,
  //   setQuantityOffField
  // ]);

  useEffect(() => {
    if (dataGridInstance) {
      setOnModalSaveClick(() => async () => {
        await dataGridInstance.instance.saveEditData();
        await new Promise((resolve) => {
          setInputProductsPieces(editableData);
          resolve();
        });
        await new Promise((resolve) => {
          if (plantId !== '8200') {
            setQuantityOffField(Number(calculateWeight(editableData)));
          }
          resolve();
        });
      });
    }
  }, [
    calculateWeight,
    dataGridInstance,
    editableData,
    plantId,
    setInputProductsPieces,
    setOnModalSaveClick,
    setQuantityField,
    setQuantityOffField
  ]);

  useEffect(() => {
    setEditableData(inputReadyProductReducer.inputPieces.map((item) => ({
      ...item,
      // eslint-disable-next-line no-restricted-globals
      percentWasteMaterial: isNaN(item.percentWasteMaterial) || item.percentWasteMaterial === 'NaN'
        ? 0 : item.percentWasteMaterial
    })));
  }, [inputReadyProductReducer]);

  const onEditorPrepared = useCallback(
    (e) => {
      if (e.parentType === 'dataRow' && e.row) {
        e.editorOptions.valueChangeEvent = 'input';
        e.editorOptions.onValueChanged = (args) => {
          e.setValue(args.value);
          const actualRow = e.row.data;
          const updatedData = editableData.map((el) => {
            if (
              el.actualPieceId === actualRow.actualPieceId
              && el.batchId === actualRow.batchId
              && el.cgId === actualRow.cgId
            ) {
              return actualRow;
            }
            return el;
          });
          setInputProductsPieces(updatedData);
          setEditableData(updatedData);

          if (isWeightable && plantId !== '8200') {
            const updatedDataSource = e.component.option('dataSource').map((item) => {
              if (item.pieceNumId === e.row.key) {
                return { ...item, [e.dataField]: args.value };
              }
              return item;
            });

            setQuantityOffField(Number(calculateWeight(updatedDataSource)));
          }
        };
      }
    },
    [editableData, setInputProductsPieces, calculateWeight, isWeightable, plantId, setQuantityOffField]
  );

  useEffect(() => {
    if (validatorRef.current) {
      setValidateInstance(validatorRef.current.instance);
    }
  }, [setValidateInstance, validatorRef]);

  useEffect(() => {
    if (validatorRef.current) {
      validatorRef.current.instance.reset();
    }
  }, [inputReadyProductReducer.okRequest]);

  return (
    <ValidationGroup ref={validatorRef}>
      <PopupMVP
        title={formatMessage('textButton').remove}
        visible={deleteConfirmModal}
      >
        <h4>
          Вы действительно хотите удалить следующие партии:
          {dataSource.selectList.map((el) => ` ${el.batchId}`).toString()}
          ?
        </h4>
        <div>
          <Button
            text={formatMessage('textButton').remove}
            type={'danger'}
            stylingMode={'contained'}
            onClick={onClickDelete}
          />
          <Button
            text={formatMessage('textButton').cancel}
            stylingMode={'contained'}
            onClick={() => {
              setDeleteConfirmModal(false);
            }}
          />
        </div>
      </PopupMVP>
      <div className={style.containing}>
        <div className={style.container}>
          <div className={style.line}>
            <span>Позиция заказа:</span>
            {renderZdvlLineId}
          </div>
          <div className={style.line}>
            <span>Нормативный документ:</span>
            {renderNormativeDoc}
          </div>
          <div className={style.line}>
            <span>Длина:</span>
            {renderLength}
          </div>
          <div className={style.line}>
            <span>№ партии ГП (СК):</span>
            {renderQcId}
          </div>
          <div className={style.line}>
            <span>Профилеразмер:</span>
            {renderProfileSize}
          </div>
          <div className={style.line}>
            <span>Дата производства:</span>
            {renderQcDate}
          </div>
          <div className={style.line}>
            <span>№ УЕ:</span>
            {renderPieceId}
          </div>
          <div className={style.line}>
            <span>Толщина:</span>
            {renderThickness}
          </div>
          <div className={style.line}>
            <span>{`Факт. кол-во${productUnitString}:`}</span>
            {renderWeightOff}
          </div>
          <div className={style.line}>
            <span>Марка стали:</span>
            {renderMark}
          </div>
          <div className={style.line} />
          { !useFactWeight && (
          <div className={style.line}>
            <span>{`Теор. кол-во${productUnitString}:`}</span>
            {renderWeight}
          </div>
          )}
          {renderInheritanceFields}
        </div>
        <div className={style.wrapper}>
          <HeaderButtonMVP headButtVisibol={headButtVisible}>
            <Button icon={'close'}
                    stylingMode={'text'}
                    onClick={closeHeaderButton}
            />
            <span className={'span-first'}>
              {`Выбрано позиций - ${dataSource.selectList.length}`}
            </span>
            {inputReadyProductReducer.inputPieces.length > 0 && (
              <>
                <Button text={formatMessage('textButton').remove}
                        type={'danger'}
                        stylingMode={'contained'}
                        onClick={() => setDeleteConfirmModal(true)}
                />
              </>
            )}
          </HeaderButtonMVP>
          <DataGridMVP className="shipment"
                       setStateStoringName={'dataGridGetSemiProducts'}
                       keyExpr="pieceNumId"
                       setDataGridInstance={setDataGridInstance}
                       onSelectionChanged={onSelectionChanged}
                       visibleButtonDateRangePicker={false}
                       visibleSummary={false}
                       visibleFilter={false}
                       focusedRowEnabled={false}
                       visibleGroupPanel={false}
                       onEditorPreparing={onEditorPrepared}
                       visibleInfoIcon={false}
                       visibleFilterPanel={false}
                       visibleHeaderFilter={false}
                       visibleSearchPanel={false}
                       selectionMode="multiple"
                       tText={formatMessage('textPeriod')}
                       dataSource={inputReadyProductReducer.inputPieces.map((item) => ({
                         ...item,
                         // eslint-disable-next-line no-restricted-globals
                         percentWasteMaterial: isNaN(item.percentWasteMaterial) || item.percentWasteMaterial === 'NaN'
                           ? 0 : item.percentWasteMaterial
                       }))}
                       loadingData={inputReadyProductReducer.loading}
                       filterDataGrid
          >
            <Editing allowUpdating mode="cell" />
            {columnDataGrid.map((item) => (
              <Column dataField={item.dataField}
                      caption={item.caption}
                      visible={!item.notVisible}
                      cellRender={item.cellRender}
                      key={item.dataField}
                      allowEditing={!!item.allowEditing}
                      dataType={item.dataType}
                      cssClass={item.cssClass}
                      width={item.width ? item.width : undefined}
              >
                {item.validation}
              </Column>
            ))}
          </DataGridMVP>
        </div>
      </div>
    </ValidationGroup>
  );
};

export default React.memo(InputModalReadyProducts);
